import { DestroyRef, inject, Injectable } from '@angular/core';
import { OidcSecurityService, LoginResponse } from 'angular-auth-oidc-client';
import { map, tap } from 'rxjs';
import { IdTokenJwt } from './models/id-token-jwt';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const REQUESTED_ROUTE = 'requestedRouteBeforeAuth';
const ERROR_CYCLE_ONCE = 'errorCycleOnce';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private oidcSecurityService = inject(OidcSecurityService);
  private router = inject(Router);

  destroyRef = inject(DestroyRef);
  isAuthenticated$ = this.oidcSecurityService.isAuthenticated$.pipe(
    takeUntilDestroyed(this.destroyRef),
    map((res) => res.isAuthenticated)
  );

  initAuth() {
    this.oidcSecurityService
      .checkAuth()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(
          (res) => {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('fromNessyBadgeLogin') === 'true') {
              localStorage.clear();
            }
            const currentPath = window.location.pathname;
            const publicRoutes = ['/error', '/logout'];
            if (res.isAuthenticated) {
              this.handleValidAuth(res);
            } else if (publicRoutes.includes(currentPath)) {
              this.loadRequestedRoute();
            } else {
              this.handleError();
            }
          },
          () => this.handleError()
        )
      )
      .subscribe();
  }

  handleValidAuth(res: LoginResponse) {
    this.checkAndHandleRoute(res);
  }

  handleError() {
    if (!localStorage.getItem(ERROR_CYCLE_ONCE)) {
      localStorage.setItem(ERROR_CYCLE_ONCE, 'true');
      this.goToLogin();
    } else {
      localStorage.removeItem(ERROR_CYCLE_ONCE);
      console.error('Already attempted to re-authenticate. Staying on error page.');
      this.router.navigateByUrl('/error');
    }
  }

  goToLogin() {
    localStorage.setItem(REQUESTED_ROUTE, window.location.pathname);
    this.oidcSecurityService.authorize();
  }

  logout() {
    this.oidcSecurityService
      .logoffAndRevokeTokens()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        localStorage.removeItem(ERROR_CYCLE_ONCE);
      });
  }

  logoutFromError() {
    this.oidcSecurityService
      .logoff()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        localStorage.removeItem(ERROR_CYCLE_ONCE);
      });
  }

  bearerToken() {
    return this.oidcSecurityService.getAccessToken();
  }

  private checkAndHandleRoute(authResult?: LoginResponse) {
    if (authResult && !this.roleIncludesStudent(authResult)) {
      this.logout();
    }

    this.loadRequestedRoute();
  }

  private loadRequestedRoute() {
    localStorage.removeItem(ERROR_CYCLE_ONCE);
    // Get landing URL from local storage
    const requestedRoute = localStorage.getItem(REQUESTED_ROUTE);
    if (requestedRoute) {
      localStorage.removeItem(REQUESTED_ROUTE);
      this.router.navigateByUrl(requestedRoute);
    }
  }

  roleIncludesStudent(res: LoginResponse) {
    if (res.idToken) {
      const decodedIdToken: IdTokenJwt = this.decodeJwt(res.idToken);
      return decodedIdToken.role.includes('Student');
    } else {
      return false;
    }
  }

  decodeJwt(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  }
}
